import {
  Alert,
  Button,
  Flexbox,
  FormField,
  Label,
  Select,
  SingleDatePicker,
  TextInput
} from "@sede-x/shell-ds-react-framework";
import {
  AddCircle,
  AlertSolid,
  CaretDown,
  CaretUp,
  IconCircleInfo,
  MinusCircle,
  Split,
  TrashAltSolid
} from "@sede-x/shell-ds-react-framework/build/esm/components/Icon/components";
import dayjs from "dayjs";
import { useState } from "react";
import { usePicklists } from "ticketing/contexts/picklists/PicklistContextProvider";
import useSearchBatches from "ticketing/hooks/useSearchBatches";
import useSearchCarriers from "ticketing/hooks/useSearchCarriers";
import useSearchFacilities from "ticketing/hooks/useSearchFacilities";
import useSearchProducts from "ticketing/hooks/useSearchProducts";
import {
  TCarrier,
  TMovement,
  TMovementGroup,
  TPdfDocument,
  TShipCode,
  TTicketInput
} from "ticketing/ticketing.types";
import {
  dealRequiresGrossVolume,
  getCurrentCSTDate,
  isCarrierRequired,
  isMovementDaily,
  isStorageDeal,
  isValidPageRange,
  MODE_OF_TRANSPORT_RAIL
} from "../../utils";
import { filterShipFromCodes, filterShipToCodes } from "../movements/shipcode-filter";
import "./TicketForm.css";
import TicketVolumesWrapper from "./TicketVolumesWrapper";

const getMaxTicketDate = (movement: TMovement | undefined) => {
  if (movement) {
    const { recDeal, delDeal } = movement;
    if (isStorageDeal(delDeal)) {
      return delDeal?.commitment?.endDate as Date;
    }
    if (isStorageDeal(recDeal)) {
      return recDeal?.commitment?.endDate as Date;
    }
  }
  return undefined;
};
const getMinTicketDate = (movement: TMovement | undefined) => {
  if (movement) {
    const { recDeal, delDeal } = movement;
    if (isStorageDeal(delDeal)) {
      return (delDeal?.commitment?.startDate as Date) ?? undefined;
    }
    if (isStorageDeal(recDeal)) {
      return (recDeal?.commitment?.startDate as Date) ?? undefined;
    }
  }
  return undefined;
};

const MAX_COMMENT_LENGTH = 35;
const MAX_PO_LENGTH = 25;
const TICKET_NUMBER_VALID_REGEX = /[^\w\s-]/gi;
const TEXT_INPUT_VALID_REGEX = /[^\x20-\x7E]+/g;
const PAGE_NUMBER_VALID_REGEX = /[^0-9\-,]/gi;
const FORM_INPUT_CLASS_NAME = "theme-form-input";

type TicketFormProps = {
  activeTicket: TTicketInput;
  activeMovementGroup?: TMovementGroup | null;
  canSplitTicket: boolean;
  canDeleteTicket: boolean;
  pdfFile: TPdfDocument;
  onTicketChanged: (ticket: TTicketInput, change: { [key: string]: unknown }) => void;
  onCopyTicket: (ticket: TTicketInput) => void;
  onDeleteTicket: (ticket: TTicketInput) => void;
};
const TicketForm = ({
  activeTicket,
  activeMovementGroup,
  canSplitTicket,
  canDeleteTicket,
  pdfFile,
  onTicketChanged,
  onCopyTicket,
  onDeleteTicket
}: TicketFormProps) => {
  const { picklists } = usePicklists();
  const [showErrors, setShowErrors] = useState(true);

  const toggleCollapsed = () => {
    onTicketChanged(activeTicket, { collapsed: !activeTicket.collapsed });
  };

  const hasMovement = !!activeMovementGroup;
  const linkedTicketsCount = activeMovementGroup?.activeMovement?.tickets?.length ?? 0;
  const ticketMaxDate =
    getMaxTicketDate(activeMovementGroup?.activeMovement) ?? getCurrentCSTDate(new Date());
  const ticketMinDate = getMinTicketDate(activeMovementGroup?.activeMovement);
  const ticketDateDisabled =
    hasMovement && linkedTicketsCount > 0 && isMovementDaily(activeMovementGroup);
  const canEditTicketDetails = !hasMovement;

  //#region filters
  const [searchBatches, batchResults, batchesLoading, , clearBatches] = useSearchBatches();

  const [searchCarriers, carrierResults, carriersLoading, , carriersClear] =
    useSearchCarriers();

  const [searchProducts, productResults, productsLoading, , clearProducts] =
    useSearchProducts();

  const [searchFacilities, facilitiesResults, facilitiesLoading, , clearFacilities] =
    useSearchFacilities();

  const [shipFromCodes, setShipFromCodes] = useState<TShipCode[]>(
    filterShipFromCodes(picklists?.shipFrom, activeMovementGroup).map(s => ({
      ...s,
      display: `${s.code}-${s.description}`
    }))
  );

  const onShipFromSearch = (searchTerm: string) => {
    setShipFromCodes(
      filterShipFromCodes(picklists?.shipFrom, activeMovementGroup)
        .filter(
          s =>
            searchTerm.length === 0 ||
            s.code.includes(searchTerm) ||
            s.description.toLowerCase().includes(searchTerm.toLowerCase())
        )
        .map(s => ({
          ...s,
          display: `${s.code}-${s.description}`
        }))
    );
  };

  const [shipToCodes, setShipToCodes] = useState<TShipCode[]>(
    filterShipToCodes(picklists?.shipTo, activeMovementGroup).map(s => ({
      ...s,
      display: `${s.code}-${s.description}`
    }))
  );

  const onShipToSearch = (searchTerm: string) => {
    setShipToCodes(
      filterShipToCodes(picklists?.shipTo, activeMovementGroup)
        .filter(
          s =>
            searchTerm.length === 0 ||
            s.code.includes(searchTerm) ||
            s.description.toLowerCase().includes(searchTerm.toLowerCase())
        )
        .map(s => ({
          ...s,
          display: `${s.code}-${s.description}`
        }))
    );
  };

  //#endregion

  const onTextInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name && value != null) {
      let newValue = value;

      switch (name) {
        case "ticketNumber":
        case "extSourceTicketRef":
          newValue = value.replace(TICKET_NUMBER_VALID_REGEX, "").trim();
          break;
        case "railcars":
          newValue = value.replace(TEXT_INPUT_VALID_REGEX, "");
          break;
        case "pageNumbers":
          newValue = value.replace(PAGE_NUMBER_VALID_REGEX, "");
          break;
        case "invoiceComment":
        case "poNumber":
          newValue = value.replace(TEXT_INPUT_VALID_REGEX, "");
          break;
        default:
          break;
      }

      onTicketChanged(activeTicket, { [name]: newValue });
    }
  };

  const onInputChange = (e: unknown, name: string) => {
    if (name === "carrier") {
      onTicketChanged(activeTicket, { carrier: e, carrierScacCode: (e as TCarrier)?.scac });
    } else {
      onTicketChanged(activeTicket, { [name]: e });
    }
  };

  const isPageRangeValid =
    pdfFile.docFile == null ||
    (activeTicket.pageNumbers != null &&
      pdfFile.numberOfPages != null &&
      isValidPageRange(activeTicket.pageNumbers, pdfFile.numberOfPages));

  const hasErrors = (activeTicket.errors?.length ?? 0) > 0;

  //#region markup
  return (
    <div className="na-product-wrapper">
      <form autoComplete="off">
        <Flexbox
          gap="5px"
          style={{
            padding: "5px",
            borderTopRightRadius: "5px",
            borderTopLeftRadius: "5px",
            backgroundColor: "var(--modal-title-bg)",
            borderBottom: "1px solid var(--modal-title-border)",
            color: "var(--modal-title-txt)"
          }}>
          <FormField label="Ticket ID:" size="small">
            <TextInput
              name={"ticketNumber"}
              autoComplete="off"
              autoCorrect="off"
              placeholder="numbers/letters/dash"
              value={activeTicket.ticketNumber ?? ""}
              onChange={onTextInputChange}
              invalid={(activeTicket.ticketNumber?.length ?? 0) <= 0}
              style={{ minWidth: "120px" }}
              className={FORM_INPUT_CLASS_NAME}
              size="small"
            />
          </FormField>

          <FormField label="Ticket Date:" size="small">
            <SingleDatePicker
              name="startDate"
              disabled={ticketDateDisabled}
              size="small"
              format="MM/DD/YYYY"
              invalid={!activeTicket.startDate}
              placeholder="mm/dd/yyyy"
              className={FORM_INPUT_CLASS_NAME}
              onChange={value => onTicketChanged(activeTicket, { startDate: value?.toDate() })}
              value={activeTicket.startDate ? dayjs(activeTicket.startDate) : null}
              allowClear={false}
              disabledDate={current => {
                const isBeforeMin = ticketMinDate
                  ? current.isBefore(dayjs(ticketMinDate), "day")
                  : false;
                const isAfterMax = ticketMaxDate
                  ? current.isAfter(dayjs(ticketMaxDate), "day")
                  : false;
                return isBeforeMin || isAfterMax;
              }}
            />
          </FormField>

          <FormField label="TMS BOL:" size="small">
            <TextInput
              name={"extSourceTicketRef"}
              autoComplete="off"
              autoCorrect="off"
              placeholder="numbers/letters/dash"
              style={{ minWidth: "120px" }}
              className={FORM_INPUT_CLASS_NAME}
              size="small"
              value={activeTicket.extSourceTicketRef ?? ""}
              onChange={onTextInputChange}
            />
          </FormField>

          <FormField label="Pages:" size="small">
            <TextInput
              name={"pageNumber"}
              autoComplete="off"
              autoCorrect="off"
              placeholder="numbers only"
              invalid={!isPageRangeValid}
              disabled={pdfFile.docFile == null}
              style={{ minWidth: "120px" }}
              className={FORM_INPUT_CLASS_NAME}
              size="small"
              onChange={onTextInputChange}
              value={activeTicket.pageNumbers ?? ""}
            />
          </FormField>
          {canSplitTicket && (
            <Button
              icon={<Split />}
              title="Copy/Split Ticket"
              sentiment="positive"
              variant="transparent"
              size="xsmall"
              iconOnly={true}
              onClick={() => onCopyTicket(activeTicket)}
              disabled={!canSplitTicket}></Button>
          )}
          <Button
            icon={<TrashAltSolid />}
            title={"Delete Ticket"}
            sentiment="negative"
            variant="transparent"
            size="xsmall"
            iconOnly={true}
            disabled={!canDeleteTicket}
            onClick={() => onDeleteTicket(activeTicket)}></Button>
        </Flexbox>
        {activeTicket && (
          <TicketVolumesWrapper
            activeTicket={activeTicket}
            onTicketsChanged={onTicketChanged}
            showGrossVolumeWarning={dealRequiresGrossVolume(
              activeMovementGroup?.activeMovement
            )}></TicketVolumesWrapper>
        )}

        {hasErrors && (
          <div style={{ marginLeft: "10px" }}>
            <Button
              icon={
                showErrors ? (
                  <MinusCircle style={{ fill: "red" }} />
                ) : (
                  <AddCircle style={{ fill: "red" }} />
                )
              }
              title={"View Errors"}
              sentiment="primary"
              variant="transparent"
              size="xsmall"
              iconOnly={true}
              onClick={() => setShowErrors(!showErrors)}></Button>
          </div>
        )}

        {showErrors && hasErrors && (
          <div style={{ padding: "10px" }}>
            <Alert sentiment="warning" iconVisibility={false} solidBgColor>
              {showErrors && hasErrors && (
                <div style={{ marginLeft: "10px" }}>
                  <ul style={{ margin: "0", padding: "0", listStyle: "none" }}>
                    {activeTicket.errors
                      ?.filter(e => e?.message)
                      .sort(
                        (e1, e2) => Number(e1?.warning ?? false) - Number(e2?.warning ?? false)
                      )
                      .map(e => (
                        <li
                          key={e?.message ?? ""}
                          style={{ textAlign: "left", display: "flex", alignItems: "center" }}>
                          <span
                            style={{
                              marginRight: "2px",
                              color: e?.error ? "red" : "black"
                            }}>
                            {e?.error ? (
                              <AlertSolid fontSize={"16px"} />
                            ) : (
                              <IconCircleInfo fontSize={"16px"} />
                            )}
                          </span>
                          <span
                            style={{
                              margin: "2px 4px",
                              color: e?.error ? "red" : "black"
                            }}>
                            {e?.message ?? ""}
                          </span>
                        </li>
                      ))}
                  </ul>
                </div>
              )}
            </Alert>
          </div>
        )}

        <div className="ticket-details-wrapper">
          <div className="ticket-details-header" onClick={() => toggleCollapsed()} role="none">
            <Label style={{ fontSize: "1rem", fontWeight: "bold", userSelect: "none" }}>
              Details
            </Label>
            <Button
              icon={activeTicket.collapsed ? <CaretDown /> : <CaretUp />}
              sentiment="primary"
              variant="transparent"
              size="xsmall"
              iconOnly={true}
              title={
                (activeTicket.collapsed ? "Expand" : "Collapse") + " Details section"
              }></Button>
          </div>

          {!activeTicket.collapsed && (
            <div className="ticket-details">
              <Label type="label">
                {isCarrierRequired(activeMovementGroup as TMovementGroup, activeTicket) && "*"}
                Carrier
              </Label>

              <Select
                //@ts-ignore
                options={carrierResults}
                value={activeTicket?.carrier?.name}
                fieldNames={{ label: "name", value: "id" }}
                onChange={(_, value) => onInputChange(value, "carrier")}
                loading={carriersLoading}
                mode="single"
                onBlur={() => carriersClear()}
                size={"small"}
                className={FORM_INPUT_CLASS_NAME}
                onSearch={e => {
                  if (e.length >= 0) {
                    searchCarriers(e.toString());
                  }
                }}
                optionFilterProp="name"
              />

              <Label type="label">Batch Name</Label>
              <Select
                disabled={!canEditTicketDetails}
                fieldNames={{ label: "name", value: "id" }}
                optionLabelProp="name"
                optionFilterProp="name"
                //@ts-ignore
                options={batchResults}
                value={activeTicket?.batch?.name}
                loading={batchesLoading}
                onChange={(_, obj) => onTicketChanged(activeTicket, { batch: obj })}
                onBlur={() => clearBatches()}
                size={"small"}
                className={FORM_INPUT_CLASS_NAME}
                onSearch={e => {
                  if (e.length >= 0) {
                    searchBatches(e.toString());
                  }
                }}
              />

              <Label type="label">SCAC Code</Label>
              <TextInput
                name={"carrierScacCode"}
                autoComplete="off"
                autoCorrect="off"
                maxLength={255}
                disabled={!activeTicket.carrier || !!activeTicket.carrierScacCode}
                placeholder="numbers/letters/dash"
                value={activeTicket?.carrierScacCode ?? ""}
                size="small"
                invalid={(activeTicket.ticketNumber?.length ?? 0) <= 0}
                style={{ minWidth: "120px", maxHeight: "30px" }}
                className={FORM_INPUT_CLASS_NAME}
              />

              <Label type="label">Product</Label>
              <Select
                fieldNames={{ label: "name", value: "id" }}
                optionLabelProp="name"
                optionFilterProp="name"
                //@ts-ignore
                options={productResults}
                disabled={!canEditTicketDetails}
                value={activeTicket?.product?.name}
                onChange={(_, obj) => onTicketChanged(activeTicket, { product: obj })}
                loading={productsLoading}
                onBlur={() => clearProducts()}
                size={"small"}
                className={FORM_INPUT_CLASS_NAME}
                onSearch={e => {
                  if (e.length >= 0) {
                    searchProducts(e.toString());
                  }
                }}
              />

              <Label type="label">Ship From</Label>
              <Select
                size={"small"}
                className={FORM_INPUT_CLASS_NAME}
                //@ts-ignore
                options={shipFromCodes}
                fieldNames={{ label: "display", value: "id" }}
                value={activeTicket?.shipFromCode?.display}
                onChange={(_, obj) => onTicketChanged(activeTicket, { shipFromCode: obj })}
                onSearch={onShipFromSearch}
              />

              <Label type="label">Ship To</Label>
              <Select
                size={"small"}
                className={FORM_INPUT_CLASS_NAME}
                //@ts-ignore
                options={shipToCodes}
                fieldNames={{ label: "display", value: "id" }}
                value={activeTicket?.shipToCode?.display}
                onChange={(_, obj) => onTicketChanged(activeTicket, { shipToCode: obj })}
                onSearch={onShipToSearch}
              />
              <Label type="label">Location</Label>
              <Select
                fieldNames={{ label: "name", value: "id" }}
                optionLabelProp="name"
                optionFilterProp="name"
                //@ts-ignore
                options={facilitiesResults}
                disabled={!canEditTicketDetails}
                value={activeTicket?.facility?.name}
                onChange={(_, obj) => onTicketChanged(activeTicket, { facility: obj })}
                loading={facilitiesLoading}
                onBlur={() => clearFacilities()}
                size={"small"}
                className={FORM_INPUT_CLASS_NAME}
                onSearch={e => {
                  if (e.length >= 0) {
                    searchFacilities(e.toString());
                  }
                }}
              />
              <Label type="label">Border Crossing</Label>
              <SingleDatePicker
                disabled={ticketDateDisabled}
                size="small"
                picker="date"
                invalid={!activeTicket.startDate}
                placeholder="MM/dd/yyyy"
                className={FORM_INPUT_CLASS_NAME}
                onChange={value =>
                  onTicketChanged(activeTicket, { borderCrossingDate: value?.toDate() })
                }
                value={
                  activeTicket.borderCrossingDate
                    ? dayjs(activeTicket.borderCrossingDate)
                    : null
                }
              />

              <Label type="label">Logistics System</Label>
              {picklists?.logisticsSystems && (
                <Select
                  fieldNames={{ label: "name", value: "id" }}
                  optionLabelProp="name"
                  optionFilterProp="name"
                  //@ts-ignore
                  options={picklists.logisticsSystems}
                  disabled={!canEditTicketDetails}
                  value={activeTicket?.logisticsSystem?.name}
                  onChange={(_, obj) => onTicketChanged(activeTicket, { logisticsSystem: obj })}
                  size={"small"}
                  className={FORM_INPUT_CLASS_NAME}
                  allowClear={true}
                />
              )}

              <Label type="label">Invoice Comments</Label>
              <TextInput
                name={"invoiceComment"}
                autoComplete="off"
                maxLength={MAX_COMMENT_LENGTH}
                onChange={onTextInputChange}
                value={activeTicket?.invoiceComment ?? ""}
                size={"small"}
                spellCheck={false}
                className={FORM_INPUT_CLASS_NAME}
                suffix={{
                  node: (
                    <Label size="small" prominence="subtle">
                      <em>
                        ({activeTicket?.invoiceComment?.length ?? 0}/{MAX_COMMENT_LENGTH})
                      </em>
                    </Label>
                  ),
                  transparent: true
                }}
              />

              <Label type="label">PO Number</Label>
              <TextInput
                name={"poNumber"}
                autoComplete="off"
                maxLength={MAX_PO_LENGTH}
                onChange={onTextInputChange}
                value={activeTicket?.poNumber ?? ""}
                size={"small"}
                spellCheck={false}
                className={FORM_INPUT_CLASS_NAME}
                suffix={{
                  node: (
                    <Label size="small" prominence="subtle">
                      <em>
                        ({activeTicket?.poNumber?.length ?? 0}/{MAX_PO_LENGTH})
                      </em>
                    </Label>
                  ),
                  transparent: true
                }}
              />
              {activeTicket.modeOfTransport?.name === MODE_OF_TRANSPORT_RAIL && (
                <>
                  <Label type="label">
                    Railcar<span style={{ color: "red" }}> *</span>
                  </Label>
                  <TextInput
                    name={"railcars"}
                    autoComplete="off"
                    disabled={false}
                    onChange={onTextInputChange}
                    value={activeTicket?.railcars ?? ""}
                    placeholder="Enter comma separated values"
                    style={{ gridColumn: "auto / span 4" }}
                    invalid={!activeTicket?.railcars}
                    size={"small"}
                    spellCheck={false}
                    className={FORM_INPUT_CLASS_NAME}
                  />
                </>
              )}
            </div>
          )}
        </div>
      </form>
    </div>
  );
  //#endregion
};

export default TicketForm;
